import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import StorageLocationAutocomplete from "./StorageLocationAutocomplete";
import { MOBILE_WAREHOUSE_LOCATION } from "../../../../constants/graphql/queries";
import OccupiedLocationModal from "./OccupiedLocationModal";
import SelectFixedLocation from "./SelectFixedLocation";

const initialLocation = { warehouse: null, warehouse_locations: [] };

const locationsVariables = {
  excludeType: "base",
  locationType: "virtual_pallet",
  order: ["location_id", "asc"],
  projectIds: [],
};

export default function SelectMobileLocationModal({ onClose, onOverride, onSubmit, warehouseId }) {
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [details, setDetails] = useState({});
  const [occupiedLocationData, setOccupiedLocationData] = useState(null);
  const [selectedMobileLocation, setSelectedMobileLocation] = useState(null);
  const [getMobileWarehouseLocation, { loading, data, called }] = useLazyQuery(
    MOBILE_WAREHOUSE_LOCATION
  );
  const mobileWarehouseLocation = data?.mobileWarehouseLocation;
  const vars = warehouseId ? { ...locationsVariables, warehouseId } : locationsVariables;

  useEffect(() => {
    if (called && !loading && mobileWarehouseLocation) {
      const occupied = mobileWarehouseLocation.occupied;
      if (occupied) {
        setOccupiedLocationData(mobileWarehouseLocation);
      } else {
        onOverride(mobileWarehouseLocation);
      }
    }
  }, [called, loading, data]);
  const handleSubmit = () => {
    const locationId = details?.locations?.[0]?.id;
    if (locationId) {
      getMobileWarehouseLocation({ variables: { id: locationId } });
    }
  };

  const handleOccupiedModalClose = () => {
    setOccupiedLocationData(null);
  };

  const handleKeepCurrentLocation = () => {
    setOccupiedLocationData(null);
    const {
      warehouseLocations: [warehouseLocation],
      warehouse,
    } = mobileWarehouseLocation;
    // Return warehouse_location object with warehouse data
    onSubmit(
      [{
        ...warehouseLocation,
        warehouse: {
          id: warehouse.id,
          warehouse_id: warehouse.warehouseId,
          name: warehouse.name,
        },
      }],
      mobileWarehouseLocation
    );
  };

  const openMoveToNewLocation = () => {
    setSelectedMobileLocation(occupiedLocationData);
    setOccupiedLocationData(null);
    setSelectedLocation(initialLocation);
  };

  const handleMoveToNewLocation = (value, details) => {
    onSubmit(value, details);
  };

  const handleChange = (value, details) => {
    setSelectedLocation(value);
    setDetails(details);
  };

  return (
    <>
      <Dialog open maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography fontSize={24} fontWeight={400} align="center" color="text.primary">
            Select Mobile Location
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <StorageLocationAutocomplete
              autoOpen
              locationsVariables={vars}
              warehouseId={warehouseId}
              enableMobileWarehouseModal={false}
              value={selectedLocation}
              onChange={handleChange}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={loading && <CircularProgress size={22} />}
            disabled={loading || !selectedLocation.warehouse_locations?.[0]?.id}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {selectedMobileLocation && (
        <SelectFixedLocation
          location={selectedMobileLocation}
          onClose={() => setSelectedMobileLocation(null)}
          onSubmit={handleMoveToNewLocation}
        />
      )}
      <OccupiedLocationModal
        open={!!occupiedLocationData}
        onClose={handleOccupiedModalClose}
        onKeepCurrentLocation={handleKeepCurrentLocation}
        onMoveToNewLocation={openMoveToNewLocation}
        locationName={occupiedLocationData?.warehouseLocations?.[0]?.locationName}
      />
    </>
  );
}
